import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { CTABand } from '../components/CTABand';
import { SectionLabel } from '../components/SectionLabel';
import { StatusTag } from '../components/StatusTag';
import { PRODUCTS } from '../data/site';
export function Products() {
  return <>
      <PageHero eyebrow="Products" title="Three systems for the records Africa cannot afford to lose." lede="Each product owns one domain end to end — property, land, and community finance — rather than fragmenting it across tools that never agree with each other." aside={<nav aria-label="Products on this page" data-id="element-361" data-mp="2x3itndvmzk08">
            <ul className="flex flex-wrap gap-x-5 gap-y-2" data-id="element-362" data-mp="2x2qyu59v5qb0">
              {PRODUCTS.map(product => <li key={product.slug} data-id="element-363" data-mp="hf0o54cq55gb">
                  <a href={`#${product.slug}`} className="text-sm text-ink-400 underline-offset-4 transition-colors duration-150 ease-out hover:text-bronze-300 hover:underline" data-id="element-364" data-mp="cecoxvecdib0">
                    {product.name}
                  </a>
                </li>)}
            </ul>
          </nav>} data-id="element-360" data-mp="2s4pelh0pbkxy" />

      {PRODUCTS.map((product, index) => <section key={product.slug} id={product.slug} className={`scroll-mt-24 border-b border-ink-100 ${index % 2 === 1 ? 'bg-ink-50' : 'bg-white'}`} data-id="element-365" data-mp="1w7b7bgqeyiw9" data-mpi={index}>
          <div className="mx-auto grid max-w-container gap-10 px-6 py-16 lg:grid-cols-2 lg:items-center lg:gap-16 lg:px-10 lg:py-24" data-id="element-366" data-mp="14hokqj4ysi52">
            <div className={index % 2 === 1 ? 'lg:order-2' : ''} data-id="element-367" data-mp="1d9gsgm2ltd5y">
              <img src={product.image} alt={`${product.name} — ${product.category}`} className="aspect-[4/3] w-full object-cover" data-id="element-368" data-mp="1db1qilo4ne8k" />
            </div>

            <div className={index % 2 === 1 ? 'lg:order-1' : ''} data-id="element-369" data-mp="kr34rfazf7kl">
              <StatusTag status={product.status} timing={product.timing} data-id="element-370" data-mp="1o2sd6tq1jf8q" />
              <h2 className="mt-5 text-3xl font-semibold leading-tight tracking-tight text-ink-900 lg:text-4xl" data-id="element-371" data-mp="1j5ifyrwetzac">
                {product.name}
              </h2>
              <p className="mt-2 text-sm uppercase tracking-[0.14em] text-ink-500" data-id="element-372" data-mp="1xvpnwdosjzws">
                {product.category}
              </p>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-ink-700" data-id="element-373" data-mp="154pl2627526p">
                {product.lede}
              </p>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-ink-600" data-id="element-374" data-mp="107fswinfacf6">
                {product.body}
              </p>

              <ul className="mt-8 divide-y divide-ink-200 border-y border-ink-200" data-id="element-375" data-mp="1e3kpbclok27d">
                {product.points.map(point => <li key={point} className="py-3 text-sm leading-relaxed text-ink-700" data-id="element-376" data-mp="2pkazt7x4zg6s">
                    {point}
                  </li>)}
              </ul>

              <div className="mt-8" data-id="element-377" data-mp="2t303vaax14ym">
                {product.to ? <Link to={product.to} className="group inline-flex items-center gap-2 bg-ink-900 px-6 py-3.5 text-sm font-medium text-white transition-colors duration-150 ease-out hover:bg-bronze-600" data-id="element-378" data-mp="2t531afeee0dd">
                    Explore {product.name}
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-379" data-mp="2o5zx0x68ry6g" />
                  </Link> : <Link to="/contact" className="inline-flex items-center gap-2 border border-ink-300 px-6 py-3.5 text-sm font-medium text-ink-800 transition-colors duration-150 ease-out hover:border-ink-900" data-id="element-380" data-mp="322n61ybgyesq">
                    Join the {product.name} early access list
                  </Link>}
              </div>
            </div>
          </div>
        </section>)}

      <section className="border-b border-ink-100" data-id="element-381" data-mp="2bseb4lre54lh">
        <div className="mx-auto max-w-container px-6 py-16 lg:px-10 lg:py-20" data-id="element-382" data-mp="26uuuxgb4uhqw">
          <SectionLabel data-id="element-383" data-mp="21vrphur8q9ie">Shared foundations</SectionLabel>
          <h2 className="mt-6 max-w-2xl text-3xl font-semibold leading-tight tracking-tight text-ink-900 lg:text-4xl" data-id="element-384" data-mp="21ye7ypfo69fe">
            What every Keystone product carries
          </h2>
          <ul className="mt-10 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-4" data-id="element-385" data-mp="2ukgsy0frqqxb">
            {[{
            title: 'Mobile money native',
            text: 'Collections and disbursements over the rails your users already trust, reconciled automatically.'
          }, {
            title: 'Works on any phone',
            text: 'SMS and WhatsApp channels for the people who will never install an app, plus a full web platform for operators.'
          }, {
            title: 'Full audit trail',
            text: 'Every change is attributable and timestamped, so disputes are settled by the record rather than by memory.'
          }, {
            title: 'Built for low bandwidth',
            text: 'Fast on intermittent connections, because that is the normal operating condition, not an exception.'
          }].map(item => <li key={item.title} className="border-t border-ink-200 pt-5" data-id="element-386" data-mp="3nx5x2zmzyak">
                <h3 className="text-base font-semibold tracking-tight text-ink-900" data-id="element-387" data-mp="7oh2o5gaszw6">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-600" data-id="element-388" data-mp="cgqqnfxurpvt">{item.text}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <CTABand title="Want early access to LandChain or SACCO Suite?" text="Tell us the market you operate in and we will bring you into the pilot group as each product opens up." primaryLabel="Get in touch" secondaryLabel="About Keystone" secondaryTo="/about" data-id="element-389" data-mp="me57vhrloip6" />
    </>;
}